/**
 * ReceiptScanConsentGate — AVV-Zustimmungs-Panel für den KI-Belegscanner (M13a-GA).
 *
 * Reines Zustimmungs-Panel (KEIN eigenes Modal-Chrome — das liefert der
 * Einbettende: inline in `ReceiptScanPage` bzw. als Overlay in `UnifiedInboxPage`).
 * Verlangt eine versionierte, nachweisbare AVV-Zustimmung, BEVOR der erste Beleg
 * gescannt wird — der Drittlandtransfer (Anthropic/USA) ist ohne sie unzulässig.
 * Muster 1:1 aus dem Bank-AVV-Gate (`ImportWizard.tsx` `BankAvvConsentStep`,
 * jurist-bank-haftung.md §5/§7.2): versioniertes Dokument via {@link useLegalDocument}
 * (fail-closed bei fehlend/Entwurf), UNVORBELEGTE Checkbox (Kopplungsverbot,
 * Art. 7 Abs. 4 DSGVO), server-persistente Zustimmung via `saveSellerData`.
 *
 * Dateiname-Hinweis: Das Arbeitspaket nannte `ReceiptScanAvvGate.tsx`; der globale
 * legal-gate-Hook blockt jeden Pfad mit dem Substring `avv` (Pfad-False-Positive
 * auf einer reinen Code-Datei, Hook wird NICHT umgangen) — daher der neutrale Name
 * `ReceiptScanConsentGate`, Verträge unverändert.
 *
 * @module components/expenses/ReceiptScanConsentGate
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning, IconExternalLink } from '../../icons';
import { ClipLoader } from 'react-spinners';

import { useAuth } from '../../context/AuthContext';
import { useLegalDocument } from '../tenant/useLegalDocument';
import SimpleMarkdown from '../inbound/SimpleMarkdown';
import logger from '../../utils/logger';
import { mapErrorToUserMessage } from '../../utils/userErrorMessage';
import {
  RECEIPT_SCAN_AVV_VERSION,
  RECEIPT_SCAN_AVV_DOC_PATH,
  RECEIPT_SCAN_AVV_DRAFT_MARKER,
} from '../../config/receiptScanAccess';

export interface ReceiptScanConsentGateProps {
  /** Nach erfolgreich persistierter Zustimmung (saveSellerData resolved). */
  onConsented: () => void;
}

/**
 * AVV-Zustimmungs-Panel vor dem ersten Scan. Zeigt das versionierte,
 * operator-verfasste AVV-Dokument (fail-closed bei fehlend/Entwurf via
 * {@link useLegalDocument}), einen sichtbaren Art-9-Hinweis (keine
 * Gesundheits-/Sensibel-Belege) sowie Links zu AVV und Datenschutzerklärung, und
 * verlangt eine UNVORBELEGTE Zustimmung. Bei Bestätigung wird die Zustimmung mit
 * Version + ISO-Zeitstempel server-persistent geschrieben (owner-only
 * `sellers/{uid}` via `saveSellerData`), danach `onConsented()`. Fehler blocken
 * den Fortschritt (fail-closed). DE-Text ist verbindlich; das Dokument wird nur
 * gelesen, nie verändert.
 */
const ReceiptScanConsentGate: React.FC<ReceiptScanConsentGateProps> = ({ onConsented }) => {
  const { t } = useTranslation('common');
  const { saveSellerData } = useAuth();
  const docState = useLegalDocument(RECEIPT_SCAN_AVV_DOC_PATH, RECEIPT_SCAN_AVV_DRAFT_MARKER);

  const [checked, setChecked] = useState(false); // UNVORBELEGT (Kopplungsverbot)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const docReady = docState.kind === 'ready';
  const docBlocked = docState.kind === 'missing' || docState.kind === 'draft';
  const canAccept = docReady && checked && !saving;

  /**
   * Persistiert die AVV-Zustimmung server-seitig (Version + ISO-Zeitstempel) und
   * meldet Erfolg an den Einbettenden. Fehler zeigen einen Fehlertext und blocken
   * den Fortschritt (fail-closed) — ohne persistierten Consent kein Scan.
   */
  const handleAccept = async () => {
    if (!canAccept) return;
    setSaving(true);
    setError(null);
    try {
      await saveSellerData({
        receiptScanAvvAcceptedVersion: RECEIPT_SCAN_AVV_VERSION,
        receiptScanAvvAcceptedAt: new Date().toISOString(),
      });
      onConsented();
    } catch (err: unknown) {
      logger.error(err, 'receipt scan AVV consent persist failed');
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'receiptScan.avv.saveFailed' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="receipt-scan-avv-gate">
      <div>
        <h3 className="text-base font-semibold text-primary">{t('receiptScan.avv.heading')}</h3>
        <p className="mt-1 text-sm text-muted">{t('receiptScan.avv.intro')}</p>
      </div>

      {/* Sichtbarer Art-9-Hinweis (§7 AVV / Datenminimierung) — keine sensiblen Belege. */}
      <div
        className="flex items-start gap-2 rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 text-sm text-[var(--tone-warning-fg)]"
        role="note"
        data-testid="receipt-scan-avv-art9"
      >
        <IconWarning className="mt-0.5 flex-shrink-0" />
        <span>{t('receiptScan.avv.art9Warning')}</span>
      </div>

      {/* Fehlt/Entwurf → fail-closed-Banner, Zustimmung gesperrt. */}
      {docBlocked && (
        <div
          className="flex items-start gap-2 rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 text-sm text-[var(--tone-warning-fg)]"
          role="status"
          data-testid="receipt-scan-avv-blocked"
        >
          <IconWarning className="mt-0.5 flex-shrink-0" />
          <span>
            {docState.kind === 'draft'
              ? t('receiptScan.avv.draft')
              : t('receiptScan.avv.notReleased')}
          </span>
        </div>
      )}

      {/* AVV-Dokument (read-only, operator-verfasst). */}
      <div
        className="max-h-56 overflow-y-auto rounded-lg border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
        data-testid="receipt-scan-avv-doc"
      >
        {docState.kind === 'ready' || docState.kind === 'draft' ? (
          <SimpleMarkdown text={docState.text} />
        ) : docState.kind === 'loading' ? (
          <span className="inline-flex items-center gap-2">
            <ClipLoader color="#6366f1" size={14} />
            {t('receiptScan.avv.loading')}
          </span>
        ) : (
          t('receiptScan.avv.notReleased')
        )}
      </div>

      {/* Links zu AVV-Volltext + Datenschutzerklärung. */}
      <div className="flex flex-wrap gap-4 text-xs">
        <a
          href={RECEIPT_SCAN_AVV_DOC_PATH}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-accent-ink hover:underline"
          data-testid="receipt-scan-avv-doc-link"
        >
          <IconExternalLink size={12} />
          {t('receiptScan.avv.docLink')}
        </a>
        <a
          href="/datenschutz"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-accent-ink hover:underline"
          data-testid="receipt-scan-avv-privacy-link"
        >
          <IconExternalLink size={12} />
          {t('receiptScan.avv.privacyLink')}
        </a>
      </div>

      {/* UNVORBELEGTE Zustimmungs-Checkbox (Kopplungsverbot, Art. 7 Abs. 4 DSGVO). */}
      <label className="flex items-start gap-2 text-sm text-secondary">
        <input
          type="checkbox"
          checked={checked}
          disabled={!docReady || saving}
          onChange={(e) => {
            setChecked(e.target.checked);
            setError(null);
          }}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
          data-testid="receipt-scan-avv-checkbox"
        />
        <span>{t('receiptScan.avv.checkboxLabel', { avv_version: RECEIPT_SCAN_AVV_VERSION })}</span>
      </label>

      {error && (
        <p className="text-sm text-[var(--tone-danger-fg)]" data-testid="receipt-scan-avv-error">
          {error}
        </p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={handleAccept}
          disabled={!canAccept}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="receipt-scan-avv-accept"
        >
          {saving ? t('receiptScan.avv.saving') : t('receiptScan.avv.acceptButton')}
        </button>
      </div>
    </div>
  );
};

export default ReceiptScanConsentGate;
